import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData, useOutletContext, useSearchParams } from "@remix-run/react";
import React, { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import prisma from "~/utils/db.server";
import { ProductRail } from "~/components/storefront/ProductRail";
import { Reveal } from "~/components/storefront/Reveal";
import { FlexCarousel } from "~/components/storefront/FlexCarousel";
import { cacheWrap } from "~/utils/cache.server";
import { detectLocale, localePath } from "~/utils/locale";
import { isPreLaunch } from "~/utils/launch";
import type { Locale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { getProductBaseName, buildSiblingsMap } from "~/utils/productSiblings";
import { renameProductTitle } from "~/utils/productTitle";
import { heroWebpSrcSet, heroMobileWebpSrcSet } from "~/utils/heroImages";
import { resolveMarket } from "~/services/markets/marketDetection.server";
import { getMarketPrices } from "~/services/markets/marketPricing.server";

const RecentlyViewed = React.lazy(
  () => import("~/components/storefront/RecentlyViewed").then((m) => ({ default: m.RecentlyViewed }))
);

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = data?.locale || "vi";
  // Production hostname fallback — pre-cutover the test.* domain was
  // baked into canonicals on every error path which Google would
  // index as the canonical version.
  const origin = (data as any)?.origin || "https://diolo.com";
  const title = locale === "vi" ? "Diolo — Trang phục pickleball nam" : "Diolo — Men's Pickleball Wear";
  const description = locale === "vi"
    ? "Trang phục pickleball nam — thiết kế tại Sài Gòn, cắt cho sân đấu, mặc được cả ngày. DIOLO 001."
    : "Men's pickleball wear — designed in Saigon, cut for the court, worn all day. DIOLO 001.";
  // T1.3: og:image must be an absolute https URL so social crawlers can fetch it.
  const ogImage = `${origin}/og/diolo-v2.png`;
  const canonicalUrl = `${origin}/`;
  return [
    { title },
    { name: "description", content: description },
    // T3.2: canonical link lets Googlebot dedupe variant URLs (?utm=...)
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: ogImage },
    { property: "og:url", content: canonicalUrl },
    { property: "og:type", content: "website" },
    // Above-fold hero image preloads — improves LCP by giving the browser
    // a head-start on the image that fills the first viewport. Both
    // mobile and desktop variants are preloaded with `media` so each
    // device only fetches what it needs. Hero #1 is the merchant-curated
    // first slide (HeroBanner falls back to /hero/{landscape,portrait}-1.jpg).
    //
    // CRITICAL (audit 2026-04-24): preload the WebP variants, NOT the
    // JPG fallbacks. The JPGs are 1.7-3.6 MB each; the WebPs are
    // 17-82 KB. The <picture> element below correctly serves the WebP
    // to modern browsers, but if we preload the JPG, the browser eats
    // the bandwidth + LCP cost for nothing — it ends up downloading
    // BOTH the JPG (via preload) AND the WebP (via <picture>).
    // Pre-fix a SG VPN customer saw "barely loading"; this is the root
    // cause. `imageSrcSet` + `imageSizes` let the browser pick the
    // narrowest variant matching the viewport (480w mobile, 768w/1280w
    // desktop) instead of always pulling the full 1920w master.
    // Preload the actual LCP hero image. The storefront default hero is the
    // single editorial frame (/editorial/hero-court.webp, served for both
    // mobile + desktop — see getDefaultHeroSlides). It is rendered as a plain
    // <img fetchpriority="high">, so preloading it here closes the late-
    // discovery gap. (T3 will move editorial imagery onto cdn.diolo.com for
    // AVIF/responsive variants; until then this is a single optimized WebP.)
    {
      tagName: "link",
      rel: "preload",
      as: "image",
      href: "/editorial/hero-court.webp",
      media: "(min-width: 640px)",
      fetchpriority: "high",
    },
    {
      tagName: "link",
      rel: "preload",
      as: "image",
      href: "/editorial/hero-court-mobile.jpg",
      media: "(max-width: 639px)",
      fetchpriority: "high",
    },
    // T3.4: WebSite schema for SearchAction sitelinks search box. The
    // companion Organization schema is emitted from the storefront layout
    // (_storefront.tsx) so it covers every page, not just the homepage.
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Diolo",
        url: origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:image", content: ogImage },
  ];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = detectLocale(request.url);
  const shop = await cacheWrap("homepage:shop", () => prisma.shop.findFirst(), { ttl: 60 });
  if (!shop) return json({ newProducts: [], bestSellers: [], allProducts: [], collections: [], locale, siblingProducts: [], instagramPosts: null, preLaunch: isPreLaunch() });

  // Audit 2026-04-29: fold instagramPosts into the cached shop-meta
  // query so the homepage loader makes one fewer round trip to PG.
  // Saves 50-150ms TTFB on cache miss; free on cache hit.
  const shopMeta = await cacheWrap(
    `homepage:shopVis:${shop.id}`,
    () => prisma.shop.findUnique({
      where: { id: shop.id },
      select: { hideSoldOutB2c: true, showOnlyActiveB2c: true, instagramPosts: true },
    }),
    { ttl: 60 }
  );
  const shopVisibility = shopMeta;
  const instagramPosts = shopMeta?.instagramPosts ?? null;

  const statusFilter = shopVisibility?.showOnlyActiveB2c === false
    ? { in: ["ACTIVE" as const, "DRAFT" as const] }
    : ("ACTIVE" as const);

  const baseWhere: any = {
    shopId: shop.id,
    isRetailEnabled: true,
    status: statusFilter,
    // Hide products with no renderable image of any kind. Historically
    // only `featuredImageUrl` mattered, but after the Shopify backfill
    // a product may have zero Sapo images and still render fine via
    // `backupImages` or admin `mediaOverrides`. So we accept any of the
    // three sources.
    OR: [
      { featuredImageUrl: { not: null } },
      { backupImages: { not: null } },
      { mediaOverrides: { not: null } },
    ],
  };

  if (shopVisibility?.hideSoldOutB2c) {
    baseWhere.variants = { some: { inventoryQuantity: { gt: 0 } } };
  }

  const productInclude = {
    variants: {
      where: { isAvailable: true },
      select: { id: true, option1: true, option2: true, price: true, compareAtPrice: true, inventoryQuantity: true },
    },
    reviews: {
      where: { isPublished: true },
      select: { rating: true },
    },
  };

  const croOrderBy: any = [
    { sortPosition: "asc" as const },
    { sapoCreatedAt: { sort: "desc" as const, nulls: "last" as const } },
  ];

  // instagramPosts is now sourced from `shopMeta` above (cached + merged
  // into the single shop-meta query) — see audit 2026-04-29.

  const [newProducts, bestSellers, allProducts, collections] = await Promise.all([
    // New Arrivals: newest by DB creation date. 2026-06-09: switched from
    // sapoCreatedAt → createdAt. sapoCreatedAt is NULL for every product
    // added after the bulk Sapo import and is the import date (2026-04-01)
    // for the rest, so `nulls: last` buried the genuinely-new products
    // (Sculpt, Monaco) at the bottom. `createdAt` is when the product first
    // entered our DB = the real new-arrival date (distinct per launch).
    cacheWrap(
      `homepage:new:${shop.id}`,
      () => prisma.product.findMany({
        where: baseWhere,
        include: productInclude,
        orderBy: [{ createdAt: "desc" as const }],
        take: 12,
      }),
      { ttl: 300 }
    ),
    // Best Sellers: ranked by RECENT sales (last 60 days), in-stock only.
    // 2026-06-09: was ranked by ALL-TIME order-item count, which surfaced old
    // hits that are now discontinued / sold out (e.g. Balance Blue Grey: 143
    // orders but 0 stock). Recent-window + a hard in-stock filter make the
    // rail reflect what's actually selling AND buyable right now.
    cacheWrap(
      `homepage:bestsellers:${shop.id}`,
      async () => {
        const since = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
        const recent = await prisma.retailOrderItem.groupBy({
          by: ["productId"],
          where: { order: { createdAt: { gte: since } } },
          _sum: { quantity: true },
          orderBy: { _sum: { quantity: "desc" as const } },
          take: 60, // over-fetch; trimmed to 12 in-stock below
        });
        const rankedIds = recent
          .map((r) => r.productId)
          .filter((id): id is string => !!id);
        if (rankedIds.length === 0) return [] as any[];
        const prods = await prisma.product.findMany({
          where: {
            ...baseWhere,
            id: { in: rankedIds },
            // A best-seller MUST be buyable — drop anything out of stock,
            // regardless of the shop's hideSoldOut setting.
            variants: { some: { inventoryQuantity: { gt: 0 } } },
          },
          include: productInclude,
        });
        const rank = new Map(rankedIds.map((id, i) => [id, i]));
        return prods
          .sort((a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9))
          .slice(0, 12);
      },
      { ttl: 300 }
    ),
    cacheWrap(
      `homepage:all:${shop.id}`,
      () => prisma.product.findMany({
        where: baseWhere,
        include: productInclude,
        orderBy: croOrderBy,
        take: 12,
      }),
      { ttl: 300 }
    ),
    cacheWrap(
      `homepage:collections:${shop.id}`,
      () => prisma.collection.findMany({
        where: { shopId: shop.id, isActive: true },
        orderBy: { sortOrder: "asc" },
        take: 6,
      }),
      { ttl: 300 }
    ),
  ]);

  // Load sibling products for color swatches + resolve the visitor's
  // market in parallel — both depend only on the data above and on
  // independent inputs, so there's no reason to await sequentially.
  // Audit 2026-05-06 (perf): on a cache-miss path, resolveMarket can
  // take 30-80 ms (cookie + GeoIP lookup) and the siblings query
  // can take 100-300 ms. Running concurrently saves the smaller of
  // the two from the critical path.
  const allLoadedProducts = [...newProducts, ...bestSellers, ...allProducts];
  const loadedIds = new Set(allLoadedProducts.map((p) => p.id));
  const baseNames = [...new Set(allLoadedProducts.map((p) => getProductBaseName(p.title)).filter(Boolean))];

  const [siblingProducts, { market }] = await Promise.all([
    baseNames.length > 0
      ? cacheWrap(
          `homepage:siblings:${shop.id}`,
          () => prisma.product.findMany({
            where: {
              shopId: shop.id,
              isRetailEnabled: true,
              status: typeof statusFilter === "string" ? statusFilter : { in: [...statusFilter.in] },
              retailPrice: { gt: 0 },
              OR: baseNames.map((base) => ({ title: { startsWith: `${base} - ` } })),
            },
            select: {
              id: true,
              title: true,
              titleEn: true,
              handle: true,
              featuredImageUrl: true,
              // Media fields forwarded so ProductCard can render a full
              // swipeable gallery when a colour swatch is selected — not
              // just the single featured image.
              images: true,
              mediaOverrides: true,
              backupImages: true,
              retailPrice: true,
              retailComparePrice: true,
              preOrderEnabled: true,
              variants: {
                where: { isAvailable: true },
                select: { id: true, price: true, inventoryQuantity: true },
              },
            },
          }),
          { ttl: 300 }
        )
      : Promise.resolve([] as any[]),
    resolveMarket(request, shop.id),
  ]);

  // Review stats
  const addReviewStats = (products: typeof newProducts) =>
    products.map((p) => {
      const reviewCount = p.reviews?.length || 0;
      const reviewAvg = reviewCount > 0
        ? Math.round((p.reviews.reduce((sum: number, r: { rating: number }) => sum + r.rating, 0) / reviewCount) * 10) / 10
        : 0;
      const { reviews, ...rest } = p;
      return { ...rest, reviewAvg, reviewCount };
    });

  // Market pricing — uses the `market` resolved above
  const allHomepageProducts = [...newProducts, ...bestSellers, ...allProducts] as any[];

  if (market) {
    const allVariants = allHomepageProducts.flatMap(
      (p: any) => p.variants.map((v: any) => ({
        id: v.id,
        price: Number(v.price),
        compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
      }))
    );

    if (allVariants.length > 0) {
      const marketPriceMap = await getMarketPrices(
        allVariants,
        market.marketId,
        shop.currency || "VND",
        shop.id
      );

      for (const p of allHomepageProducts) {
        for (const v of p.variants) {
          const mp = marketPriceMap.get(v.id);
          if (mp) {
            v.price = mp.price;
            v.compareAtPrice = mp.compareAtPrice;
          }
        }
        if (p.variants.length > 0) {
          const firstMp = marketPriceMap.get(p.variants[0].id);
          if (firstMp) {
            (p as any).retailPrice = firstMp.price;
            (p as any).retailComparePrice = firstMp.compareAtPrice;
          }
        }
      }
    }
  }

  // T1.3: expose absolute origin so meta functions can build fully-qualified
  // og:image URLs. Social crawlers reject relative paths.
  // Fly terminates TLS at the edge, so `request.url` on the inside is http://.
  // Force https:// since production is always HTTPS.
  const origin = new URL(request.url).origin.replace(/^http:\/\//, "https://");

  // Diolo display rename (Monaco Skorts → The Monaco Skorts etc).
  // Applied AFTER sibling base-name keying so the raw Sapo titles
  // were available for grouping. See utils/productTitle.ts.
  const renameItem = (p: any) => {
    if (!p) return p;
    if (p.title) p.title = renameProductTitle(p.title);
    if (p.titleEn) p.titleEn = renameProductTitle(p.titleEn);
    return p;
  };
  for (const p of newProducts as any[]) renameItem(p);
  for (const p of bestSellers as any[]) renameItem(p);
  for (const p of allProducts as any[]) renameItem(p);
  for (const sp of siblingProducts as any[]) renameItem(sp);

  return json(
    { preLaunch: isPreLaunch(),
      newProducts: addReviewStats(newProducts),
      bestSellers: addReviewStats(bestSellers),
      allProducts: addReviewStats(allProducts),
      siblingProducts: siblingProducts.filter((sp) => !loadedIds.has(sp.id)),
      collections,
      instagramPosts,
      locale,
      origin,
    },
    {
      headers: {
        // Audit 2026-04-29: bumped s-maxage from 600 → 3600 so the
        // Cloudflare edge cache hangs onto the homepage for an hour
        // before revalidating. Saves 200-400ms TTFB for repeat VN
        // visitors. The 300s browser cache + 1h SWR window are
        // unchanged. Admin product/Sapo writes invalidate via the
        // existing cacheWrap purge wiring (committed Apr 7).
        "Cache-Control": "public, max-age=300, s-maxage=3600, stale-while-revalidate=3600",
      },
    }
  );
};

// ─── Hero Slides ─── //
function getDefaultHeroSlides(locale: Locale) {
  // 2026-08-31: the menswear shoot landed (STEP 7 done) — full-colour
  // night-court hero from the launch campaign (Kinetic Tee Black + Tempo
  // Short Grey, chain-link fence, DIOLO wordmark burned into the frame
  // top-right by the design comp). `plain: true` renders a bare <img>
  // (single hand-tuned WebP, no srcset variants); the wordmark is baked
  // in, so the overlay carries only the launch copy lower-third.
  // On mobile the same frame centre-crops to the model via object-cover
  // (wordmark falls outside the crop) — dedicated portrait art can slot
  // into imgMobile later without touching the layout.
  return [
    {
      img: "/editorial/hero-court.webp",
      // Portrait crop of the same frame, wordmark-free (Hayden 2026-09-01:
      // "the banner for the mobile hero image… just crop it").
      imgMobile: "/editorial/hero-court-mobile.jpg",
      alt: locale === "vi"
        ? "Áo Kinetic Tee và quần Tempo Short Diolo — sân pickleball ban đêm"
        : "Diolo Kinetic Tee and Tempo Short — night pickleball court",
      eyebrow: isPreLaunch() ? "DIOLO 001 — 09.09" : (locale === "vi" ? "DIOLO 001 — đã mở bán" : "DIOLO 001 — available now"),
      title: "Born to Compete",
      subtitle: "",
      cta: { label: t("shopNow", locale), to: "/products" },
      ctaSecondary: null as null | { label: string; to: string },
      hasBuiltInText: false,
      plain: true,
      mono: false,
    },
  ];
}

function getHeroSlides(locale: Locale, dbHeroSlides: any) {
  if (dbHeroSlides && Array.isArray(dbHeroSlides) && dbHeroSlides.length > 0) {
    // Audit 2026-05-06: db-driven hero slides used `alt: slide.title || ""`,
    // which fell back to empty string when an admin uploaded a hero
    // without a title (decorative). An empty alt on a hero image is an
    // a11y miss + SEO miss. Fall back to a brand-generic alt so screen
    // readers + crawlers always have something to describe.
    return dbHeroSlides.map((slide: any) => {
      const title = slide.title || "";
      const subtitle = slide.subtitle || "";
      // If the merchant left both title + subtitle empty, assume the
      // image already has burned-in text (e.g. "MONACO SUMMER / IT'S
      // TIME FOR ICE CREAM" baked into the JPG by the design team).
      // Setting hasBuiltInText=true skips the overlay so the burned-in
      // typography is the only text shown — no double-text artefact.
      const hasBuiltInText = !title && !subtitle;
      return {
        img: slide.image || slide.img || "",
        imgMobile: slide.imageMobile || slide.image || slide.img || "",
        alt: slide.altText || slide.title || (locale === "vi"
          ? "Diolo — Trang phục thể thao cao cấp"
          : "Diolo — Men's pickleball wear"),
        title,
        subtitle,
        hasBuiltInText,
        cta: {
          label: slide.cta1Label || t("shopNow", locale),
          to: slide.cta1Link || "/products",
        },
        ctaSecondary: {
          label: slide.cta2Label || t("explore", locale),
          to: slide.cta2Link || "/products",
        },
      };
    });
  }
  return getDefaultHeroSlides(locale);
}

// ─── Hero Banner Component ─── //
// Diolo rebrand 2026-06-10: full-viewport editorial hero. Content sits in
// the lower third, centered — tiny uppercase eyebrow, tight title-case
// headline, single white CTA. Multi-slide support (admin heroSlides) and
// auto-advance are retained; chrome (arrows) is gone — slides crossfade.
function HeroBanner() {
  const { locale, heroSlides: dbHeroSlides } = useOutletContext<any>();
  const heroSlides = getHeroSlides(locale, dbHeroSlides);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % heroSlides.length);
  }, [heroSlides.length]);

  useEffect(() => {
    if (isPaused || heroSlides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, isPaused, heroSlides.length]);

  return (
    <section
      className="relative h-[88svh] min-h-[480px] overflow-hidden bg-black sm:h-[100svh]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {heroSlides.map((slide: any, i: number) => {
        const monoClass = slide.mono ? "grayscale contrast-[1.05]" : "";
        return (
          <div
            key={slide.title || i}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 2 : 0, pointerEvents: i === current ? "auto" : "none" }}
          >
            {slide.plain ? (
              /* <picture> so each breakpoint downloads exactly one file:
                 phones get the portrait crop, sm+ the full landscape comp. */
              <picture className="absolute inset-0 h-full w-full">
                {slide.imgMobile && slide.imgMobile !== slide.img ? (
                  <source media="(max-width: 639px)" srcSet={slide.imgMobile} />
                ) : null}
                <img
                  src={slide.img}
                  alt={slide.alt}
                  width={1920}
                  height={1080}
                  className={`absolute inset-0 h-full w-full object-cover ${monoClass}`}
                  style={slide.objectPosition ? { objectPosition: slide.objectPosition } : undefined}
                  loading={i === 0 ? "eager" : "lazy"}
                  {...(i === 0 ? { fetchpriority: "high" as const } : {})}
                />
              </picture>
            ) : (
              <>
                {/* Desktop picture */}
                <picture className="absolute inset-0 hidden h-full w-full sm:block">
                  <source type="image/webp" srcSet={heroWebpSrcSet(slide.img)} sizes="100vw" />
                  <img
                    src={slide.img}
                    alt={slide.alt}
                    width={1920}
                    height={1080}
                    className={`absolute inset-0 h-full w-full object-cover ${monoClass}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    {...(i === 0 ? { fetchpriority: "high" as const } : {})}
                  />
                </picture>
                {/* Mobile picture */}
                <picture className="absolute inset-0 h-full w-full sm:hidden">
                  <source type="image/webp" srcSet={heroMobileWebpSrcSet(slide.imgMobile || slide.img)} sizes="100vw" />
                  <img
                    src={slide.imgMobile || slide.img}
                    alt={slide.alt}
                    width={768}
                    height={1024}
                    className={`absolute inset-0 h-full w-full object-cover ${monoClass}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    {...(i === 0 ? { fetchpriority: "high" as const } : {})}
                  />
                </picture>
              </>
            )}

            {/* Legibility scrim — bottom-weighted */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/10" />

            {/* Centered lower-third content */}
            {!slide.hasBuiltInText && (
              <div
                className="absolute inset-x-0 bottom-14 z-10 flex flex-col items-center px-6 text-center sm:bottom-16"
                style={{
                  opacity: i === current ? 1 : 0,
                  transform: i === current ? "translateY(0)" : "translateY(12px)",
                  transition: "opacity 0.7s ease, transform 0.7s ease",
                }}
              >
                {slide.eyebrow ? (
                  <p className="df-eyebrow mb-2 text-white/90 normal-case tracking-normal text-[13px]">{slide.eyebrow}</p>
                ) : null}
                <h1 className="df-headline mb-5 text-[32px] text-white sm:text-[40px]">{slide.title}</h1>
                <Link
                  to={localePath(slide.cta.to, locale)}
                  className="inline-flex h-10 items-center justify-center bg-white px-7 text-[11px] font-normal uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white"
                >
                  {slide.cta.label}
                </Link>
              </div>
            )}

            {/* Built-in-text slides still need their CTA */}
            {slide.hasBuiltInText && (
              <div className="absolute inset-x-0 bottom-14 z-10 flex justify-center sm:bottom-16">
                <Link
                  to={localePath(slide.cta.to, locale)}
                  className="inline-flex h-10 items-center justify-center bg-white px-7 text-[11px] font-normal uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white"
                >
                  {slide.cta.label}
                </Link>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ─── Main Homepage Component ─── //
export default function StorefrontHome() {
  const data = useLoaderData<typeof loader>();
  const { newProducts, bestSellers, allProducts, siblingProducts } = data;
  const preLaunch = data.preLaunch !== false;
  const { locale } = useOutletContext<any>();
  const [searchParams] = useSearchParams();
  const accountDeleted = searchParams.get("account_deleted") === "1";

  // Best-sellers rail falls back to the merchant-curated "all" list on a
  // young store with no 60-day sales history yet.
  const bestSellerRail = (bestSellers?.length ? bestSellers : allProducts) as any[];

  // Build siblings map for colour swatches
  const allProductsCombined = useMemo(
    () => [...newProducts, ...bestSellers, ...allProducts, ...(siblingProducts || [])].filter(Boolean),
    [newProducts, bestSellers, allProducts, siblingProducts]
  );
  const siblingsMap = useMemo(() => buildSiblingsMap(allProductsCombined as any), [allProductsCombined]);

  // One tile per style (4-SKU catalog) — on-model front shots, straight
  // to the black colourway PDP. Replaced the activity tiles (Training/
  // Court/Running/Lounge): Diolo is all-court pickleball, and activity
  // filters over 4 styles were dead ends (Hayden, 2026-08-31).
  // Every colourway of 001 as a swipeable card (FlexCarousel below).
  const styleTiles = [
    { label: "Drift Tee", sub: "Black", price: "950.000₫", img: "/products/drift-tee-black-5.jpg", to: "/products/drift-tee-black" },
    { label: "Drift Tee", sub: "Ash Navy", price: "950.000₫", img: "/products/drift-tee-navy-5.jpg", to: "/products/drift-tee-navy" },
    { label: "Kinetic Tee", sub: "Black", price: "800.000₫", img: "/products/kinetic-tee-black-5.jpg", to: "/products/kinetic-tee-black" },
    { label: "Kinetic Tee", sub: "Grey", price: "800.000₫", img: "/products/kinetic-tee-grey-5.jpg", to: "/products/kinetic-tee-grey" },
    { label: "Kinetic Tee", sub: "Navy", price: "800.000₫", img: "/products/kinetic-tee-navy-5.jpg", to: "/products/kinetic-tee-navy" },
    { label: "Tempo Short", sub: "Black", price: "1.500.000₫", img: "/products/tempo-short-black-3.jpg", to: "/products/tempo-short-black" },
    { label: "Tempo Short", sub: "Grey", price: "1.500.000₫", img: "/products/tempo-short-grey-3.jpg", to: "/products/tempo-short-grey" },
    { label: "Baseline Polo", sub: "Black", price: "950.000₫", img: "/products/baseline-polo-black-5.jpg", to: "/products/baseline-polo-black" },
    { label: "Baseline Polo", sub: "Navy", price: "950.000₫", img: "/products/baseline-polo-navy-6.jpg", to: "/products/baseline-polo-navy" },
  ];

  return (
    <div>
      {/* Account deletion notice */}
      {accountDeleted && (
        <div className="bg-black text-white px-4 py-4 text-center text-xs font-normal tracking-[0.05em]">
          Your account has been successfully deleted. We&apos;re sorry to see you go.
        </div>
      )}

      {/* ─── 1. Full-viewport hero ─── */}
      <HeroBanner />

      {/* ─── 2. New-arrivals rail ─── */}
      <Reveal>
        <ProductRail
          products={newProducts as any[]}
          siblingsMap={siblingsMap as any}
          locale={locale}
          link={{
            label: locale === "vi" ? "XEM HÀNG MỚI VỀ" : "SHOP NEW ARRIVALS",
            to: localePath("/products?tag=new", locale),
          }}
        />
      </Reveal>

      {/* ─── 3. Shop the Collection — swipe between the cards; they lean and flex with momentum ─── */}
      <Reveal y={32}>
        <FlexCarousel locale={locale} items={styleTiles.map((t) => ({ ...t, to: localePath(t.to, locale) }))} />
      </Reveal>

      {/* ─── 4. Split editorial banners ─── */}
      <section className="grid grid-cols-1 sm:grid-cols-2">
        {[
          {
            eyebrow: locale === "vi" ? "Sinh ra để thi đấu" : "Engineered for play",
            title: "DIOLO 001",
            cta: locale === "vi" ? "KHÁM PHÁ" : "EXPLORE",
            img: "/editorial/night-action.jpg",
            to: "/products",
          },
          {
            eyebrow: locale === "vi" ? "Ngoài sân đấu" : "Off the court",
            title: locale === "vi" ? "Ngoài Giờ" : "Off Duty",
            cta: locale === "vi" ? "TÌM HIỂU" : "DISCOVER",
            img: "/editorial/night-walk.jpg",
            to: "/products/drift-tee-black",
          },
        ].map((tile, i) => (
          <Reveal key={tile.title} delay={(i % 2) * 110} y={32} className="block">
          <Link to={localePath(tile.to, locale)} className="group relative block overflow-hidden bg-black">
            <div className="relative aspect-[4/5] sm:aspect-auto sm:h-[86svh]">
              <img
                src={tile.img}
                alt={tile.title}
                width={960}
                height={1200}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-start gap-3 p-6 sm:p-10">
                <div>
                  <p className="text-[13px] font-normal text-white/90">{tile.eyebrow}</p>
                  <h2 className="df-headline mt-1 text-[26px] text-white sm:text-[32px]">{tile.title}</h2>
                </div>
                <span className="inline-flex h-10 items-center justify-center bg-white/20 px-6 text-[11px] font-normal uppercase tracking-[0.12em] text-white backdrop-blur-sm transition-colors group-hover:bg-white group-hover:text-black">
                  {tile.cta}
                </span>
              </div>
            </div>
          </Link>
          </Reveal>
        ))}
      </section>

      {/* ─── 5. Full-bleed technical banner ─── */}
      <Reveal className="block">
      <section className="relative h-[70svh] min-h-[400px] overflow-hidden bg-black">
        <img
          src="/products/kinetic-tee-black-18.jpg"
          alt=""
          aria-hidden="true"
          width={1600}
          height={900}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-[13px] font-normal text-white/90">
            {locale === "vi" ? "Chất liệu kỹ thuật cao" : "Technical fabric construction"}
          </p>
          <h2 className="df-headline mt-1 text-[30px] text-white sm:text-[38px]">
            {locale === "vi" ? "Sinh Ra Cho Mỗi Ngày" : "Built For Every Day"}
          </h2>
          <Link
            to={localePath("/products", locale)}
            className="mt-6 inline-flex h-10 items-center justify-center bg-white px-7 text-[11px] font-normal uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white"
          >
            {locale === "vi" ? "MUA NGAY" : "SHOP NOW"}
          </Link>
        </div>
      </section>
      </Reveal>

      {/* ─── 6. Best-sellers rail ─── */}
      <Reveal>
        <ProductRail
          products={bestSellerRail}
          siblingsMap={siblingsMap as any}
          locale={locale}
          link={{
            label: locale === "vi" ? "XEM BÁN CHẠY NHẤT" : "SHOP BEST SELLERS",
            to: localePath("/products", locale),
          }}
        />
      </Reveal>

            {/* ─── 7. 09.09 launch date block — stadium campaign frame.
            Purely a date announcement while the pre-launch buy-gate is on
            (~/utils/launch.ts); keep or retire after launch day. ─── */}
      {preLaunch && (
        <Reveal className="block">
        <section className="relative h-[80svh] min-h-[440px] overflow-hidden bg-black">
          <img
            src="/editorial/night-stadium.jpg"
            alt=""
            aria-hidden="true"
            width={1122}
            height={1402}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-[50%_25%]"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
            <p className="text-[13px] font-normal text-white/90">
              {locale === "vi" ? "Đánh dấu ngày" : "Save the date"}
            </p>
            <h2 className="df-headline mt-2 text-[64px] leading-none text-white sm:text-[96px]">09.09</h2>
            <p className="mt-3 text-[13px] text-white/80">
              {locale === "vi" ? "DIOLO 001 mở bán lúc 21:00" : "DIOLO 001 goes on sale at 9 pm"}
            </p>
            <Link
              to={localePath("/products", locale)}
              className="mt-8 inline-flex h-10 items-center justify-center bg-white px-7 text-[11px] font-normal uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white"
            >
              {locale === "vi" ? "XEM 001" : "EXPLORE 001"}
            </Link>
          </div>
        </section>
        </Reveal>
      )}

      {/* ─── 8. Recently Viewed ─── */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <RecentlyViewed />
      </Suspense>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
